import { useCallback, useState } from 'react';

import { useGetStaticAssetUrl } from '@change-corgi/core-react/assets';

import {
	useDecisionMakerProfilePageData,
	useDMProfilePagePaginatedResponsesData,
	useDMProfilePagePaginatedTargetedPetitionsData,
} from '#src/app/pages/decisionMaker/profile/pageContext';
import type { UsState } from '#src/app/pages/decisionMaker/profile/pageContext';
import { useProcessedImageUrl } from '#src/app/shared/hooks/image';
import { useQueryParamValue } from '#src/app/shared/hooks/location';

type Result = ModelHookResult<
	{
		id: string;
		displayName: string;
		title: string | null;
		type: string;
		districtStateText: string;
		photoUrl: string;
		verified: boolean;
		responsesCount: number;
		petitionsCount: number;
		isDescriptionClampable: { desktop: boolean; mobile: boolean };
		descriptionClamped: boolean;
		descriptionText: string | null;
		displayAbuseLink: boolean;
	},
	{ handleShowFullTextClick: () => void }
>;

function transformLocation(
	district: string | null | undefined,
	state: string | null | undefined,
	usStates: readonly UsState[],
): string {
	const x = [];
	if (district && /\d/.test(district)) {
		x.push(`District ${district}`);
	} else if (district) {
		x.push(district);
	}
	if (state) x.push(usStates.find((usState) => usState.stateCode === state)?.displayName);
	return x.join(', ');
}

function getDescriptionText(
	description: string | null | undefined,
	descriptionAi: string | null | undefined,
	isDmrlEnabled: boolean,
	summaryLong: { header?: string | null; body?: string | null } | null | undefined,
): string | null {
	return (
		description ||
		descriptionAi ||
		(isDmrlEnabled && summaryLong ? [summaryLong.header, summaryLong.body].filter(Boolean).join(' ') : null)
	);
}

export function useProfileCard(): Result {
	const getStaticAssetUrl = useGetStaticAssetUrl();
	const MAX_DESCRIPTION_LENGTH = {
		mobile: 120,
		desktop: 290,
	};

	const showDmrl = useQueryParamValue('showDmrl');

	const {
		data: {
			description,
			descriptionAi,
			displayName,
			district,
			id,
			photo,
			state,
			title,
			verifiedAt,
			type,
			enrichedData,
			featureConfigs: { dmReputationLayer },
			usStates,
		},
	} = useDecisionMakerProfilePageData();

	const {
		data: { totalCount: responsesCount },
	} = useDMProfilePagePaginatedResponsesData();
	const {
		data: { totalCount: petitionsCount },
	} = useDMProfilePagePaginatedTargetedPetitionsData();

	const isDmrlEnabled = dmReputationLayer.enabled || showDmrl === 'true';
	const displayAbuseLink = !(enrichedData && isDmrlEnabled);
	const dmProfileImage = useProcessedImageUrl(photo?.userLarge);

	const descriptionText = getDescriptionText(description, descriptionAi, isDmrlEnabled, enrichedData?.summaryLong);

	const districtStateText = transformLocation(district, state, usStates);

	const clampableMobile = Number(descriptionText?.length) > MAX_DESCRIPTION_LENGTH.mobile;
	const clampableDesktop = Number(descriptionText?.length) > MAX_DESCRIPTION_LENGTH.desktop;

	const isDescriptionClampable = {
		mobile: clampableMobile,
		desktop: clampableDesktop,
	};

	const [descriptionClamped, setDescriptionClamped] = useState(clampableMobile || clampableDesktop);

	const handleShowFullTextClick = useCallback(() => {
		setDescriptionClamped((curState) => !curState);
	}, []);

	return {
		data: {
			id,
			displayName,
			title,
			type,
			districtStateText,
			photoUrl: dmProfileImage || getStaticAssetUrl('/profile-img/default-user-profile.svg'),
			verified: !!verifiedAt,
			descriptionText,
			descriptionClamped,
			isDescriptionClampable,
			responsesCount,
			petitionsCount,
			displayAbuseLink,
		},
		actions: {
			handleShowFullTextClick,
		},
	};
}
